/**
 * Every word the player reads that is not already in a content JSON file
 * (doc 18 §8).
 *
 * Two reasons this is one file rather than three hundred literals:
 *
 * 1. **Localization becomes a data task.** English only at v1 (doc 13 §D), but
 *    the day someone wants German, this file and the content JSONs are the
 *    whole job — not a grep through twelve modules.
 * 2. **The tone contract becomes reviewable.** Doc 01 asks for serious water and
 *    unserious words, and at most one gag per screen. You cannot hold a voice
 *    consistent across a codebase; you can hold it across a file you can read
 *    top to bottom. Every joke in the game is now on one page.
 *
 * A `.ts` file rather than `.json`: the parameterised lines are functions, and a
 * function in JSON is a string with a bug in it.
 *
 * **What deliberately stays at its call site:** fragments that are a number and
 * a unit (`${cost} ALLOY`), and the derived stat rows in `hudstate.ts`, which
 * are assembled out of names and numbers that already live in content JSON.
 * Pulling a unit out of a template makes both ends worse and localizes nothing.
 */

export const S = {
  // -- shared ---------------------------------------------------------------
  notYet: 'NOT YET',

  title: {
    name: 'KRILL ISSUES',
    tagline: "IT'S NOT A SKILL ISSUE. IT'S A KRILL ISSUE.",
    footer: 'ARROWS + ENTER · THE OCEAN IS ALREADY RUNNING',
    noColony: 'NO COLONY',
    descend: 'DESCEND',
    descendContinue: 'DESCEND — CONTINUE',
    descendHint: 'A FRESH COLONY ON THE SHELF',
    abandon: 'ABANDON THE RUN',
    abandonHint: 'START THE DEPTH OVER. PEARLS STAY EARNED.',
    colony: 'COLONY COMMAND',
    colonyHint: 'SPEND PEARLS. RESPEC IS FREE, ALWAYS.',
    codex: 'CODEX',
    codexHint: 'WHO YOU HAVE MET, AND WHAT THEY DO.',
    slots: 'COLONY SLOTS',
    slotsHint: 'THREE COLONIES. SWITCH, COPY, OR START CLEAN.',
    settings: 'SETTINGS',
    settingsHint: 'DISPLAY, FEEL, CALM CURRENTS, AND YOUR SAVE.',
    thisColony: 'THIS COLONY',
    inEffect: 'IN EFFECT',
    pearls: 'PEARLS',
    earnedEver: 'EARNED EVER',
    nodesOwned: 'NODES OWNED',
    runs: 'RUNS',
    clearedShelf: 'CLEARED THE SHELF',
    speciesMet: 'SPECIES MET',
    treeEmpty: 'NOTHING YET — THE TREE IS EMPTY.',
    assistOn: 'CALM CURRENTS ON',
    savedJustNow: 'SAVED JUST NOW',
  },

  slots: {
    colonyAlpha: 'COLONY ALPHA',
    colonyBeta: 'COLONY BETA',
    colonyGamma: 'COLONY GAMMA',
    colonySlots: 'COLONY SLOTS',
    threeColoniesDo: 'THREE COLONIES. THEY DO NOT SHARE PEARLS, AND THEY NEVER MEET.',
    enterPlaysDel: 'ENTER PLAYS · DEL ERASES · ESC GOES BACK',
    noColonyLoaded: 'NO COLONY LOADED',
    startCleanColony: 'START A CLEAN COLONY HERE — NO PEARLS, NO NODES, NO EXCUSES.',
    new: 'NEW',
    colony: 'COLONY',
    eraseThisColony: 'ERASE THIS COLONY?',
    erase: 'ERASE IT',
    keep: 'KEEP IT',
    neverCleared: 'NEVER CLEARED',
    runProgress: 'RUN IN PROGRESS',
    recoveredFromBad: 'RECOVERED FROM A BAD WRITE',
  },
  depth: {
    trench: 'THE TRENCH',
    runWaitingDown: 'A RUN IS WAITING DOWN THERE.',
    pickPressure: 'PICK YOUR PRESSURE.',
    enterDescendsP: 'ENTER DESCENDS · P SETS PRESSURE ON A CLEARED DEPTH · ESC GOES BACK',
    locked: 'LOCKED',
    busy: 'BUSY',
    cleared: 'CLEARED',
    shelf: 'THE SHELF',
    shoalsOldSnapjaw: '12 SHOALS · OLD SNAPJAW GATES ACT 2 · THE REEF TYRANT ENDS IT',
    gloom: 'THE GLOOM',
    spawnEdgesCloaked: '3 EDGES · CLOAKED FISH · THE PALE LURE, THEN THE CALF',
    floor: 'THE FLOOR',
    spawnEdgesThings: '4 EDGES · THINGS THAT HEAL · THE MOTHER, THEN THE SOVEREIGN',
    descendingWith: 'DESCENDING WITH',
    kit: 'KIT',
    coreHp: 'CORE HP',
    storageCap: 'STORAGE CAP',
    towers: 'TOWERS',
  },
  colony: {
    nothingRefund: 'NOTHING TO REFUND.',
    colonyCommand: 'COLONY COMMAND',
    breadthPowerKnowing: 'BREADTH, POWER, AND KNOWING THINGS.',
    enterBuysR: 'ENTER BUYS · R RESPECS (FREE, INSTANT, UNLIMITED) · ESC GOES BACK',
    owned: 'OWNED',
    soon: 'SOON',
    ownedRefundedFull: 'OWNED — REFUNDED IN FULL BY A RESPEC',
    notWaterYet: 'NOT IN THE WATER YET — ARRIVES WITH ITS CONTENT',
    enterBuy: 'ENTER TO BUY IT',
    price: 'PRICE',
    nodesOwned: 'NODES OWNED',
    pearls: 'PEARLS',
  },
  codex: {
    codex: 'CODEX',
    endorsements: 'THEY HAVE ENDORSEMENTS.',
    fishLinkedinUnlocks: 'FISH LINKEDIN UNLOCKS THE NUMBERS.',
    arrowsBrowseEsc: 'ARROWS BROWSE · ESC GOES BACK',
    boss: 'BOSS',
    unmet: 'UNMET',
    notMetYet: 'NOT MET YET',
    killOneIts: 'KILL ONE AND ITS FILE OPENS. +5 PEARLS THE FIRST TIME.',
    metStatBlock: 'YOU HAVE MET IT. THE STAT BLOCK IS BEHIND FISH LINKEDIN — 15 PEARLS IN COLONY COMMAND.',
    armor: 'ARMOR',
    speed: 'SPEED',
    chew: 'CHEW',
    swift: 'SWIFT',
    yesZappyLoves: 'YES — ZAPPY LOVES THESE',
    role: 'ROLE',
    boss2: 'THE BOSS',
    miniBoss: 'MINI-BOSS',
    behaviour: 'BEHAVIOUR',
    huntsEconomy: 'HUNTS YOUR ECONOMY',
    grab: 'GRAB',
    escorts: 'ESCORTS',
    enrage: 'ENRAGE',
  },
  pressure: {
    shelf: 'THE SHELF',
    gloom: 'THE GLOOM',
    floor: 'THE FLOOR',
    choseThisWe: 'YOU CHOSE THIS. WE HAVE IT IN WRITING.',
    enterTogglesEsc: 'ENTER TOGGLES · ESC GOES BACK',
    noPressure: 'NO PRESSURE',
    off: 'OFF',
    tiersActive: 'TIERS ACTIVE',
    pearlsFrom: 'PEARLS FROM',
  },
  results: {
    landlordDeadDeposit: 'THE LANDLORD IS DEAD. THE DEPOSIT IS NOT COMING BACK.',
    fishFiledComplaint: 'THE FISH FILED A COMPLAINT. IT WAS UPHELD.',
    colonyNowArtificial: 'YOUR COLONY IS NOW AN ARTIFICIAL REEF. CONGRATULATIONS?',
    oceanWouldLike: 'THE OCEAN WOULD LIKE TO THANK YOU FOR THE SNACKS.',
    shelfCleared: 'THE SHELF: CLEARED',
    colonyCrushed: 'COLONY CRUSHED',
    reefTyrantDown: 'THE REEF TYRANT IS DOWN. THE PAPERWORK IS ENORMOUS.',
    enterPicksPearls: 'ENTER PICKS · THE PEARLS ARE ALREADY BANKED',
    seeWhatGot: 'SEE WHAT YOU GOT',
    colonyCommand: 'COLONY COMMAND',
    spendPearlsBefore: 'SPEND THE PEARLS BEFORE THE NEXT DESCENT.',
    descendAgain: 'DESCEND AGAIN',
    freshColonyShelf: 'A FRESH COLONY ON THE SHELF.',
    title: 'TITLE',
    run: 'THE RUN',
    fightsCleared: 'FIGHTS CLEARED',
    coreLeft: 'CORE LEFT',
    timeUnder: 'TIME UNDER',
    pearlsBanked: 'PEARLS BANKED',
    pearlsTotal: 'PEARLS TOTAL',
    towerXp: 'TOWER XP',
    noTowerLanded: 'NO TOWER LANDED A SHOT. BOLD.',
  },
  settings: {
    saveImportedWelcome: 'SAVE IMPORTED. WELCOME BACK.',
    settings: 'SETTINGS',
    oceanDoesNot: 'THE OCEAN DOES NOT NEGOTIATE. THE MENU DOES.',
    enterChangesRow: 'ENTER CHANGES A ROW · VOLUMES STEP IN TENS · CTRL+V PASTES A SAVE · ESC GOES BACK',
    master: 'MASTER',
    audio: 'AUDIO',
    everythingIncludingParts: 'EVERYTHING, INCLUDING THE PARTS YOU LIKE.',
    music: 'MUSIC',
    droneGetsFloor: 'THE DRONE. IT GETS A FLOOR WHEN THE FIGHTING STARTS.',
    sfx: 'SFX',
    harpoonsMortarsLure: 'HARPOONS, MORTARS, AND THE LURE. FOUR OF ANY ONE SOUND AT A TIME, EVER.',
    ambience: 'AMBIENCE',
    waterItselfSteps: 'THE WATER ITSELF. IT STEPS BACK 6 DB WHEN SOMETHING IS ANNOUNCED.',
    display: 'DISPLAY',
    uiScale: 'UI SCALE',
    fullscreen: 'FULLSCREEN',
    off: 'OFF',
    oceanLargeWindow: 'THE OCEAN IS LARGE. YOUR WINDOW DOES NOT HAVE TO BE SMALL.',
    edgeScroll: 'EDGE SCROLL',
    panShovingPointer: 'PAN BY SHOVING THE POINTER AT THE EDGE — 8 PX MARGIN, NEVER OVER THE HOTBAR.',
    minimap: 'MINIMAP',
    bottomRightPanel: 'THE BOTTOM-RIGHT PANEL, WITH THE RIM RING AND EVERY DOT ON IT.',
    feel: 'FEEL',
    screenShake: 'SCREEN SHAKE',
    mortarsPxBosses: 'MORTARS 1 PX, BOSSES 2, THE CORE 3. NOTHING ELSE SHAKES, EVER.',
    flashIntensity: 'FLASH INTENSITY',
    clampsBrightEffects: 'CLAMPS BRIGHT EFFECTS AND CHAIN-LIGHTNING JITTER. NO FULL-SCREEN FLASH EXISTS AT ANY SETTING.',
    damageNumbers: 'DAMAGE NUMBERS',
    oneNumberPer: 'ONE NUMBER PER TARGET PER 200 MS, 24 ON SCREEN AT MOST. CRITS ARE BIGGER AND YELLOW.',
    reducedMotion: 'REDUCED MOTION',
    bannersHoldInstead: 'ON: BANNERS HOLD INSTEAD OF SLIDING, TARGETS STOP BLINKING, THE DRIFT STILLS.',
    autoFollowsSystem: 'AUTO FOLLOWS YOUR SYSTEM SETTING. IT IS CURRENTLY ASKING FOR MOTION.',
    game: 'GAME',
    calmCurrents: 'CALM CURRENTS',
    autoLure: 'AUTO-LURE',
    offNothingArrives: 'OFF: NOTHING ARRIVES UNTIL YOU FLASH THE LURE. THE ORDER IS ENTIRELY YOURS.',
    confirmBeforeSell: 'CONFIRM BEFORE SELL',
    xOnceAsk: 'X ONCE TO ASK, X AGAIN TO SELL. FOR HANDS THAT MOVE FASTER THAN MINDS.',
    data: 'DATA',
    exportSaveClipboard: 'EXPORT SAVE TO CLIPBOARD',
    copyFishAnother: 'COPY YOUR FISH TO ANOTHER COMPUTER.',
    importPressCtrl: 'IMPORT: PRESS CTRL+V',
    pasteSaveOver: 'PASTE A SAVE OVER THIS SLOT. THE OLD ONE IS OVERWRITTEN.',
    exportRunLog: 'EXPORT RUN LOG',
    lastRunsAs: 'THE LAST 20 RUNS AS JSON — DEPTH, PULL ORDER, TOWERS, WHERE IT ENDED. LOCAL UNTIL YOU SEND IT.',
    replayFirstDescent: 'REPLAY THE FIRST DESCENT',
    reArmOnboarding: 'RE-ARM THE ONBOARDING CARDS ON YOUR NEXT RUN. FOR THE PLAYER WHO SKIPPED THEM.',
    nothingReplayYet: 'NOTHING TO REPLAY YET — THE CARDS ARE STILL ARMED.',
    resetPressEnter: 'RESET: PRESS ENTER AGAIN',
    resetThisColony: 'RESET THIS COLONY',
    startThisSlot: 'START THIS SLOT OVER FROM NOTHING. IT ASKS TWICE.',
    back: 'BACK',
    oceanHasAgreed: 'THE OCEAN HAS AGREED TO CHILL. SLIGHTLY.',
    calmCurrentsOff: 'CALM CURRENTS OFF. GOOD LUCK DOWN THERE.',
    firstDescentArmed: 'THE FIRST DESCENT IS ARMED AGAIN. START A RUN ON THE SHELF.',
    colonyGoneSettings: 'THE COLONY IS GONE. THE SETTINGS SURVIVED, WHICH IS SOMETHING.',
    browserSaidNo: 'THE BROWSER SAID NO TO FULLSCREEN. IT DOES THAT.',
    nothingExport: 'NOTHING TO EXPORT.',
    saveCopiedClipboard: 'SAVE COPIED TO THE CLIPBOARD.',
    browserRefusedClipboard: 'THE BROWSER REFUSED THE CLIPBOARD. TRY AGAIN AFTER A CLICK.',
  },
  run: {
    sixShoalsCircle: 'SIX SHOALS CIRCLE. ONE OF THEM IS A MISTAKE. YOU HOLD THE LURE.',
    threeEdgesNow: 'THREE EDGES NOW. THE FISH BRING THEIR OWN LIGHTS, AND THEY TURN THEM OFF.',
    nothingDownHere: 'NOTHING DOWN HERE IS SORRY.',
    backDown: 'BACK DOWN',
    clickShoalWorld: 'CLICK A SHOAL (WORLD OR MINIMAP) · SPACE PULLS IT IN',
    spaceFlashLure: 'SPACE TO FLASH THE LURE',
    lockedBuyColony: 'LOCKED — BUY IT IN COLONY COMMAND FIRST.',
    selectShoalRim: 'SELECT A SHOAL AT THE RIM (OR ITS MINIMAP DOT) — THEN SPACE PULLS IT.',
    xAgainSell: 'X AGAIN TO SELL IT. THE PANIC TAX DOES NOT ASK TWICE.',
    soldNoRefunds: 'SOLD. NO REFUNDS ON REGRET.',
    pausedPResume: 'PAUSED — P TO RESUME',
    leaveColony: 'LEAVE THE COLONY?',
    runSavedLast: 'THE RUN IS SAVED AT THE LAST PAYDAY.',
    saveLeave: 'SAVE & LEAVE',
    keepFighting: 'KEEP FIGHTING',
  },
  ev: {
    it: 'IT',
    act: 'ACT 2',
    newShoalsJoin: 'NEW SHOALS JOIN THE RIM · THE BOSS IS AMONG THEM',
    volleyAwayBudget: 'VOLLEY AWAY. THE BUDGET APPROVES.',
    reeling: 'REELING IN',
    wall: 'THE WALL',
    lurePulsedSomething: 'THE LURE PULSED. SOMETHING ANSWERED.',
    rushHour: 'RUSH HOUR',
    doubleReel: ' · DOUBLE REEL',
    payday: 'PAYDAY',
    brownoutAllSlower: 'BROWNOUT — ALL SLOWER',
    powerRestoredLights: 'POWER RESTORED. THE LIGHTS APOLOGISE.',
    depthYoursNext: 'THE DEPTH IS YOURS. THE NEXT ONE IS NOT.',
    colonyCrushed: 'COLONY CRUSHED',
    landlordDead: 'THE LANDLORD IS DEAD',
    coreUnderFire: 'CORE UNDER FIRE',
  },
  boot: {
    somethingImplodedNot: 'SOMETHING IMPLODED. NOT YOU. PROBABLY.',
    pressurizing: 'PRESSURIZING…',
    floodingChamber: 'FLOODING THE CHAMBER…',
    printingFish: 'PRINTING FISH…',
    findingColony: 'FINDING YOUR COLONY…',
    descending: 'DESCENDING',
    uncaughtError: 'UNCAUGHT ERROR',
    unhandledRejection: 'UNHANDLED REJECTION',
    pearlsAreDisk: 'YOUR PEARLS ARE ON DISK. THEY WERE BANKED THE MOMENT YOU EARNED THEM.',
    copyDiagnostics: 'COPY DIAGNOSTICS',
    exportSave: 'EXPORT SAVE',
    noSaveThis: 'NO SAVE IN THIS SLOT',
    reload: 'RELOAD',
    copied: 'COPIED.',
    browserRefusedClipboard: 'THE BROWSER REFUSED THE CLIPBOARD. SELECT THE TEXT BELOW INSTEAD.',
  },
  hotbar: {
    stab: 'STAB',
    zap: 'ZAP',
    yeet: 'YEET',
    lasr: 'LASR',
    fan: 'FAN',
    cncl: 'CNCL',
    wall: 'WALL',
    rig: 'RIG',
    melt: 'MELT',
    cook: 'COOK',
    tap: 'TAP',
    refn: 'REFN',
    gen: 'GEN',
    pyl: 'PYL',
    depo: 'DEPO',
    spa: 'SPA',
  },
  props: {
    armored: 'ARMORED',
    flatDamageReduction: 'FLAT DAMAGE REDUCTION ON EVERY HIT. MINIMUM 1 GETS THROUGH.',
    bigHitsBeat: 'BIG HITS BEAT SMALL ONES. SKEWER KEBAB IGNORES ARMOUR OUTRIGHT.',
    swift: 'SWIFT',
    threeTilesSecond: 'THREE TILES A SECOND OR BETTER. IT WILL BE PAST YOU BEFORE YOU AIM.',
    slowStunShove: 'SLOW IT, STUN IT, OR SHOVE IT. THE FAN ENJOYER DOES ALL THREE.',
    cloaked: 'CLOAKED',
    noTowerCan: 'NO TOWER CAN TARGET IT UNLESS SOMETHING REVEALS IT. SPLASH STILL LANDS, AT HALF.',
    coreLightsSix: 'THE CORE LIGHTS SIX TILES. THE FLASHLIGHT OF TRUTH LIGHTS MORE. THE SURGE LIGHTS EVERYTHING.',
    regen: 'REGEN',
    healsEverySecond: 'IT HEALS EVERY SECOND. ANY SINGLE HIT OF 25 OR MORE STOPS THAT FOR TWO.',
    burstNotTrickle: 'BURST, NOT TRICKLE. MORTARS AND TORPEDOES INTERRUPT; THE LASER OUT-DAMAGES.',
  },
  hud: {
    power: '⚡ POWER',
    capacitor: 'CAPACITOR',
    max: 'MAX',
  },
} as const;

/**
 * Lines that take a number or a name. Functions, because that is what they are —
 * and because a translator needs to see the whole sentence to move the number.
 */
export const L = {
  fightsCleared: (n: number): string => `${n} FIGHTS CLEARED`,
  savedMinutes: (n: number): string => `SAVED ${n} MIN AGO`,
  savedHours: (n: number): string => `SAVED ${n} H AGO`,
  savedDays: (n: number): string => `SAVED ${n} D AGO`,
  pearls: (n: number): string => `${n} PEARLS`,
  pearlsGained: (n: number): string => `+${n} PEARLS`,
  runsLogged: (n: number): string => `${n} RUNS`,
  runLogCopied: (n: number): string => `${n} RUNS COPIED. PASTE THEM AT US IF YOU LIKE.`,
  tierUpgraded: (name: string, tier: number): string => `${name} — TIER ${tier}. IT LOOKS PLEASED.`,
  wasEaten: (name: string): string => `${name} WAS EATEN.`,
  destroyed: (name: string): string => `${name} DESTROYED`,
  bitten: (name: string): string => `${name} BITTEN`,
  bossHasTower: (boss: string): string => `${boss} HAS A TOWER`,
  bossHugging: (boss: string): string => `${boss} IS HUGGING A TOWER. AGGRESSIVELY.`,
  bitThrough: (what: string): string => `IT BIT THROUGH ${what}. 500 DAMAGE. ONE BITE.`,
  wentUnder: (name: string): string => `${name} WENT UNDER. IT IS NOT GONE.`,
  riled: (name: string): string => `${name} IS RILED. IT TOOK THAT PERSONALLY.`,
  cameAnyway: (name: string): string => `${name} CAME ANYWAY. NOBODY ASKED IT TO.`,
  towersDark: (n: number): string => `${n} TOWERS DARK`,
  pressurePulse: (n: number): string => `PRESSURE PULSE — ${n} GRID TOWERS JUST WENT DARK.`,
  brownout: (demand: number, supply: number, pct: number): string =>
    `BROWNOUT — DEMAND ${demand} OVER SUPPLY ${supply}. EVERYTHING AT ${pct}%.`,
  resetStakes: (pearls: number, nodes: number): string =>
    `${pearls} PEARLS, ${nodes} NODES AND EVERY CLEAR. GONE. NO EXPORT IS TAKEN FOR YOU.`,
  autoLureOn: (secs: number): string =>
    `THE RIM PULLS THE NEXT SHOAL AFTER ${secs} S IF YOU HAVE NOT. NEVER THE FIRST, NEVER A BOSS.`,
  assistOn: (bonus: number, per: number, cap: number): string =>
    `CORE +${bonus}% HP · +${per}% MORE PER FAILED RUN, CAP +${cap}%`,
  assistOff: (bonus: number): string => `TURNS ON CORE +${bonus}% HP. PEARLS AND XP ARE UNAFFECTED.`,
} as const;
